import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import { useNavigate } from "react-router-dom";
import StatusBadge from "../components/StatusBadge.jsx";
import "../styles/Reviews.css";

function fmtDate(d) {
  return d ? new Date(d).toLocaleString("en-IN") : "—";
}

// Reviews page for Auditors to review submitted replies and accept/reject them
export default function Reviews() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("SUBMITTED");
  const [selectedReply, setSelectedReply] = useState(null);
  const [reviewDecision, setReviewDecision] = useState("ACCEPTED");
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [clarificationPoints, setClarificationPoints] = useState("");
  const [paraDetail, setParaDetail] = useState(null);

  // When a reply modal opens, fetch the full para detail so the reviewer
  // sees all the audit context, not just the title.
  useEffect(() => {
    if (!selectedReply?.para) {
      setParaDetail(null);
      return;
    }
    let cancelled = false;
    api.get(`/paras/${selectedReply.para}/`).then((r) => {
      if (!cancelled) setParaDetail(r.data);
    }).catch(() => {
      if (!cancelled) setParaDetail(null);
    });
    return () => { cancelled = true; };
  }, [selectedReply]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = { page_size: 100 };
    if (filter) params.review_status = filter;
    api
      .get("/replies/", { params })
      .then((r) => setReplies(r.data.results || r.data))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 3000);
  };

  const submitReview = async () => {
    if (reviewDecision === "CLARIFICATION" && !clarificationPoints.trim()) {
      window.alert(t("clarificationRequiredAlert"));
      return;
    }
    setBusy(true);
    try {
      // For CLARIFICATION, combine the required points with optional remarks
      // into a single review_remarks payload (backend has one remarks field).
      let remarks = reviewRemarks.trim();
      if (reviewDecision === "CLARIFICATION") {
        const points = clarificationPoints.trim();
        remarks = remarks
          ? `Clarification required:\n${points}\n\nAdditional notes:\n${remarks}`
          : `Clarification required:\n${points}`;
      }
      await api.post(`/replies/${selectedReply.id}/review/`, {
        review_status: reviewDecision,
        review_remarks: remarks,
      });
      setSelectedReply(null);
      setReviewRemarks("");
      setClarificationPoints("");
      setReviewDecision("ACCEPTED");
      flash(t("reviewSuccess"));
      load();
    } catch (e) {
      window.alert(e.response?.data?.detail || t("reviewFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-reviews">
      <div className="page-header">
        <div>
        </div>
      </div>

      {msg && (
        <div className="badge green" style={{ marginBottom: 12, display: "block", padding: "10px 16px" }}>
          {msg}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="section-tabs" data-testid="review-filter-tabs">
        {[
          { key: "SUBMITTED", label: t("pendingReview"), count: replies.length },
          { key: "ACCEPTED", label: t("statusACCEPTED") },
          { key: "REJECTED", label: t("statusREJECTED") },
          { key: "CLARIFICATION", label: t("clarification") },
          { key: "", label: t("all") },
        ].map((tab) => (
          <div
            key={tab.key}
            className={"section-tab" + (filter === tab.key ? " active" : "")}
            onClick={() => setFilter(tab.key)}
            data-testid={`tab-${tab.key || "all"}`}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Replies List */}
      {loading ? (
        <div className="center-screen">
          <div className="spinner"></div>
        </div>
      ) : replies.length === 0 ? (
        <div className="card empty-state">
          <p>{t("noRepliesStatus")}</p>
        </div>
      ) : (
        <div className="replies-grid">
          {replies.map((r) => (
            <div key={r.id} className="reply-card" data-testid={`reply-card-${r.id}`}>
              <div className="reply-header">
                <div>
                  <h3 className="reply-para">
                    {r.para_no} <span className="muted">· {r.para_audit_year}</span>
                  </h3>
                  <p className="reply-title muted">{r.para_title}</p>
                </div>
                <StatusBadge status={r.review_status} />
              </div>

              <div className="reply-meta">
                <span><strong>{t("by")}:</strong> {r.officer_name || "—"}</span>
                <span><strong>{t("submitted")}:</strong> {fmtDate(r.submitted_at)}</span>
              </div>

              <div className="reply-explanation">
                <strong>{t("explanation")}:</strong>
                <p>{r.explanation}</p>
              </div>

              {r.evidence && r.evidence.length > 0 && (
                <div className="reply-evidence">
                  <strong>{t("evidence")} ({r.evidence.length}):</strong>
                  <div className="evidence-list">
                    {r.evidence.map((ev) => (
                      <a
                        key={ev.id}
                        href={ev.file}
                        target="_blank"
                        rel="noreferrer"
                        className="tag-pill"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📎 {ev.doc_type_display} {ev.description ? `· ${ev.description}` : ""}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {r.review_remarks && (
                <div className="review-remarks">
                  <strong>{t("reviewRemarks")}:</strong>
                  <p>{r.review_remarks}</p>
                  <small className="muted">
                    {t("reviewedBy")} {r.reviewed_by_name} · {fmtDate(r.reviewed_at)}
                  </small>
                </div>
              )}

              <div className="reply-actions">
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => navigate(`/paras/${r.para}`)}
                  data-testid={`view-para-${r.id}`}
                >
                  {t("viewPara")}
                </button>
                {r.review_status === "SUBMITTED" && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setSelectedReply(r)}
                    data-testid={`review-btn-${r.id}`}
                  >
                    {t("reviewNow")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selectedReply && createPortal(
        <div className="modal-overlay" onClick={() => setSelectedReply(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("reviewReply")} — {selectedReply.para_no}</h3>

            <div className="review-summary">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px 16px",
                  marginBottom: 10,
                }}
              >
                <p style={{ margin: 0 }}>
                  <strong>{t("paraNo")}:</strong> {selectedReply.para_no}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>{t("year")}:</strong> {selectedReply.para_audit_year}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>{t("officer")}:</strong> {selectedReply.officer_name}
                </p>
                {paraDetail?.department && (
                  <p style={{ margin: 0 }}>
                    <strong>{t("department")}:</strong> {paraDetail.department}
                  </p>
                )}
              </div>
              {paraDetail?.description && (
                <div style={{ marginBottom: 8 }}>
                  <strong>{t("description")}:</strong>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      marginTop: 4,
                      padding: 8,
                      background: "#fff",
                      borderRadius: 4,
                      border: "1px solid #e5e7eb",
                      fontSize: 13,
                    }}
                  >
                    {paraDetail.description}
                  </div>
                </div>
              )}
              {paraDetail?.audit_remarks && (
                <div>
                  <strong>{t("auditRemarks")}:</strong>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      marginTop: 4,
                      padding: 8,
                      background: "#fff",
                      borderRadius: 4,
                      border: "1px solid #e5e7eb",
                      fontSize: 13,
                    }}
                  >
                    {paraDetail.audit_remarks}
                  </div>
                </div>
              )}
            </div>

            <div className="field">
              <label>{t("explanation")}</label>
              <div className="review-explanation-box">{selectedReply.explanation}</div>
            </div>

            <div className="field">
              <label>
                {t("evidence")}{" "}
                <span className="muted" style={{ fontWeight: "normal" }}>
                  ({selectedReply.evidence?.length || 0})
                </span>
              </label>
              {selectedReply.evidence && selectedReply.evidence.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: 10,
                    background: "#fafbfc",
                  }}
                >
                  {selectedReply.evidence.map((ev) => {
                    const fileName = ev.file
                      ? decodeURIComponent(
                          ev.file.split("/").pop().split("?")[0] || t("file")
                        )
                      : t("file");
                    const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName);
                    return (
                      <div
                        key={ev.id}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                          padding: 8,
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 4,
                        }}
                      >
                        {isImage && ev.file && (
                          <a href={ev.file} target="_blank" rel="noreferrer">
                            <img
                              src={ev.file}
                              alt={ev.description || fileName}
                              style={{
                                width: 56, height: 56, objectFit: "cover",
                                borderRadius: 4, border: "1px solid #e5e7eb",
                              }}
                            />
                          </a>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>
                            {ev.doc_type_display || ev.doc_type}
                          </div>
                          <div
                            className="muted"
                            style={{
                              fontSize: 12,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={fileName}
                          >
                            {fileName}
                          </div>
                          {ev.description && (
                            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                              {ev.description}
                            </div>
                          )}
                        </div>
                        {ev.file && (
                          <a
                            href={ev.file}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline"
                            style={{ fontSize: 12, padding: "4px 10px" }}
                          >
                            {t("view")}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="muted" style={{ fontSize: 13 }}>
                  {t("noEvidence")}
                </div>
              )}
            </div>

            <div className="field">
              <label>{t("decision")}</label>
              <select
                value={reviewDecision}
                onChange={(e) => setReviewDecision(e.target.value)}
                data-testid="review-decision-select"
              >
                <option value="ACCEPTED">{t("acceptReply")}</option>
                <option value="REJECTED">{t("rejectReply")}</option>
                <option value="CLARIFICATION">{t("requestClarification")}</option>
              </select>
            </div>

            {reviewDecision === "CLARIFICATION" && (
              <div className="field">
                <label>
                  {t("clarificationPrompt")}{" "}
                  <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <textarea
                  value={clarificationPoints}
                  onChange={(e) => setClarificationPoints(e.target.value)}
                  placeholder={t("clarificationPrompt")}
                  rows={4}
                  data-testid="clarification-points-input"
                />
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {t("clarificationHint")}
                </div>
              </div>
            )}

            <div className="field">
              <label>
                {reviewDecision === "CLARIFICATION"
                  ? t("additionalNotes")
                  : t("reviewRemarksLabel")}
              </label>
              <textarea
                value={reviewRemarks}
                onChange={(e) => setReviewRemarks(e.target.value)}
                placeholder={t("reviewRemarksPlaceholder")}
                data-testid="review-remarks-input"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn"
                onClick={() => {
                  setSelectedReply(null);
                  setClarificationPoints("");
                  setReviewRemarks("");
                  setReviewDecision("ACCEPTED");
                }}
                data-testid="cancel-review-btn"
              >
                {t("cancel")}
              </button>
              <button
                className="btn btn-primary"
                disabled={
                  busy ||
                  (reviewDecision === "CLARIFICATION" &&
                    !clarificationPoints.trim())
                }
                onClick={submitReview}
                data-testid="submit-review-btn"
              >
                {busy ? t("submitting") : t("submitReview")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
